/**
 * The autocue's roll (ADR 096): the text moves down on its own, like a prompter, at one of eight speeds. A speed is
 * lines per second of the presenter's text, so it reads the same at every letter size: level 2 is a measured news
 * read, level 4 a fast one, level 8 a sprint. The buttons beside ▲ ▼ work like a prompter's controller: + starts the
 * roll and speeds it up, − slows it down and at zero stops it, ⏸/▶ pauses and resumes at the last speed; the
 * keyboard's + and − do the same, R pauses and resumes.
 */
export const ROLL_LEVELS = [0.25, 0.4, 0.6, 0.9, 1.3, 1.8, 2.5, 3.5] as const;
export const ROLL_MAX = ROLL_LEVELS.length;
/** The speed R resumes at before any was chosen. */
export const ROLL_DEFAULT = 2;
/** The text's line height, as its style has it (`.ac-read`). */
const LINE_HEIGHT = 1.38;

/** Pixels per second for a speed level (1..4) at a letter size in pixels; 0 for a stopped roll. */
export function rollPxPerSec(level: number, fontSizePx: number): number {
  if (level <= 0) return 0;
  const lps = ROLL_LEVELS[Math.min(ROLL_MAX, Math.max(1, Math.round(level))) - 1]!;
  return lps * fontSizePx * LINE_HEIGHT;
}

/** The + button or key: from stopped to the slowest speed, then faster, up to the fastest. */
export const fasterRoll = (level: number): number => Math.min(ROLL_MAX, Math.max(0, level) + 1);

/** The − button or key: slower, and from the slowest speed to a stop. */
export const slowerRoll = (level: number): number => Math.max(0, level - 1);

/**
 * The scroll for a frame: whole pixels only (browsers round a scroll position), the rest carried to the next frame,
 * so a slow roll still moves evenly instead of stalling.
 */
export function rollStep(pxPerSec: number, dtMs: number, carry: number): { move: number; carry: number } {
  const total = carry + (pxPerSec * dtMs) / 1000;
  const move = Math.trunc(total);
  return { move, carry: total - move };
}
